import { humanize } from '@ideon-api/ideon-ui'
import { carrierLabel } from './carrier-label'
import { featureLabel } from './feature-label'

type Json = Record<string, unknown>
type Side = Json | null | undefined

export type EntryLike = { resourceType: string; diff: { before?: Side; after?: Side } }
export type DiffLike = { before?: Side; after?: Side }

export type ScalarDelta = { field: string; before: unknown; after: unknown }
export type ListDelta = { field: string; added: Array<string | number>; removed: Array<string | number> }
export type Delta = ScalarDelta | ListDelta

const ABSENT = '—'
// The permission set is already shown in its own column.
const HIDDEN_FIELDS = new Set(['permission_set_id'])
// Fields the entry title already names; repeating them in the delta adds nothing, unless an
// update changed one (the title shows only the new value).
const TITLE_FIELDS: Record<string, readonly string[]> = {
  plan_entitlement: ['product_line', 'audience', 'year', 'quarter'],
  carrier_entitlement: ['product_line', 'carrier'],
  feature_entitlement: ['feature'],
}

const text = (v: unknown): string => (v === null || v === undefined ? '' : String(v))

export function entryTitle({ resourceType, diff }: EntryLike): string {
  const side: Json = { ...diff.before, ...diff.after }
  const fallback = humanize(resourceType)

  switch (resourceType) {
    case 'plan_entitlement': {
      const quarter = text(side.quarter)
      const year = [text(side.year), quarter && !quarter.startsWith('Q') ? `Q${quarter}` : quarter].filter(Boolean).join(' ')
      const parts = [humanize(text(side.product_line)), humanize(text(side.audience)), year].filter(Boolean)
      return parts.length > 0 ? parts.join(' · ') : fallback
    }
    case 'feature_entitlement':
      return featureLabel(text(side.feature)) || fallback
    case 'carrier_entitlement': {
      const parts = [humanize(text(side.product_line)), carrierLabel(text(side.carrier))].filter(Boolean)
      return parts.length > 0 ? parts.join(' · ') : fallback
    }
    case 'network_entitlement':
      return 'Network Entitlements'
    default:
      return fallback
  }
}

const isList = (v: unknown): v is Array<string | number> =>
  Array.isArray(v) && v.every((x) => typeof x === 'string' || typeof x === 'number')

const canonical = (v: unknown): string =>
  JSON.stringify(v, (_k, val) =>
    val && typeof val === 'object' && !Array.isArray(val)
      ? Object.fromEntries(Object.entries(val).sort(([a], [b]) => a.localeCompare(b)))
      : val,
  )

const norm = (v: unknown) => (v === null || v === undefined ? undefined : v)
const shown = (v: unknown) => (norm(v) === undefined ? ABSENT : v)

export function entryDelta(diff: DiffLike, resourceType = ''): Delta[] {
  const titled = new Set(TITLE_FIELDS[resourceType] ?? [])
  const before: Json = diff.before ?? {}
  const after: Json = diff.after ?? {}
  const updated = Boolean(diff.before && diff.after)
  // An update logs only the changed columns in `after`, so a key absent there is unchanged, not removed.
  const keys = diff.before && diff.after ? Object.keys(after) : [...Object.keys(before), ...Object.keys(after)]
  const fields = [...new Set(keys)].filter((f) => !HIDDEN_FIELDS.has(f) && (updated || !titled.has(f)))

  return fields.flatMap((field): Delta[] => {
    const b = norm(before[field])
    const a = norm(after[field])
    const bList = b === undefined ? [] : b
    const aList = a === undefined ? [] : a

    if (isList(bList) && isList(aList)) {
      const added = [...new Set(aList.filter((x) => !bList.includes(x)))]
      const removed = [...new Set(bList.filter((x) => !aList.includes(x)))]
      return added.length > 0 || removed.length > 0 ? [{ field, added, removed }] : []
    }
    if (canonical(b) === canonical(a)) return []
    return [{ field, before: shown(b), after: shown(a) }]
  })
}

// A feature value is stored as {enabled: x}; staff read it as a switch, not as JSON.
export function displayValue(v: unknown): string {
  if (v === true) return 'On'
  if (v === false) return 'Off'
  if (v !== null && typeof v === 'object') {
    const keys = Object.keys(v)
    if (!Array.isArray(v) && keys.length === 1 && keys[0] === 'enabled') return displayValue((v as Json).enabled)
    return JSON.stringify(v)
  }
  return String(v)
}

// The values of a states list are obviously states, so that field carries no label.
export function fieldLabel(field: string): string {
  if (field === 'states' || field === 'value') return ''
  if (field === 'exclude_states') return 'Excluded states'
  return humanize(field)
}

export const ABSENT_VALUE = ABSENT
